import React from "react";
import { rgba } from "polished";
import styled, { css } from "styled-components";
import { lowEmphasisOpacity } from "../../../settings";
import breakpoint from "../../../utils/breakpoint";
import getBorderColor from "../../../utils/getBorderColor";
import getBorderRadius from "../../../utils/getBorderRadius";
import getBoxShadow from "../../../utils/getBoxShadow";
import getColor from "../../../utils/getColor";
import getTextColor from "../../../utils/getTextColor";
import space, { spaceFactor } from "../../../utils/space";
import transition from "../../../utils/transition";
import unit from "../../../utils/unit";
import IconAtm from "../../atoms/IconAtm";
import { cssTextAtm } from "../../atoms/TextAtm/TextAtm.styled";
import TooltipMol from "../TooltipMol";
var LoaderSVG = function LoaderSVG(props) {
  return /*#__PURE__*/React.createElement("svg", props, /*#__PURE__*/React.createElement("g", {
    fill: "none",
    fillRule: "evenodd",
    strokeWidth: "2"
  }, /*#__PURE__*/React.createElement("circle", {
    cx: "22",
    cy: "22",
    r: "1"
  }, /*#__PURE__*/React.createElement("animate", {
    attributeName: "r",
    begin: "0s",
    dur: "1.8s",
    values: "1; 20",
    calcMode: "spline",
    keyTimes: "0; 1",
    keySplines: "0.165, 0.84, 0.44, 1",
    repeatCount: "indefinite"
  }), /*#__PURE__*/React.createElement("animate", {
    attributeName: "stroke-opacity",
    begin: "0s",
    dur: "1.8s",
    values: "1; 0",
    calcMode: "spline",
    keyTimes: "0; 1",
    keySplines: "0.3, 0.61, 0.355, 1",
    repeatCount: "indefinite"
  })), /*#__PURE__*/React.createElement("circle", {
    cx: "22",
    cy: "22",
    r: "1"
  }, /*#__PURE__*/React.createElement("animate", {
    attributeName: "r",
    begin: "-0.9s",
    dur: "1.8s",
    values: "1; 20",
    calcMode: "spline",
    keyTimes: "0; 1",
    keySplines: "0.165, 0.84, 0.44, 1",
    repeatCount: "indefinite"
  }), /*#__PURE__*/React.createElement("animate", {
    attributeName: "stroke-opacity",
    begin: "-0.9s",
    dur: "1.8s",
    values: "1; 0",
    calcMode: "spline",
    keyTimes: "0; 1",
    keySplines: "0.3, 0.61, 0.355, 1",
    repeatCount: "indefinite"
  }))));
};
LoaderSVG.defaultProps = {
  viewBox: "0 0 44 44",
  xmlns: "http://www.w3.org/2000/svg",
  stroke: "currentColor"
};
export var backgroundColor = "backgroundLight";
export var typographyName = "medium2_s";
var horizontalGutterSpace = 2;
var borderWidth = 1;
var typographyInactiveEmphasis = "medium";
var suffixTypographyEmphasis = "medium";
export var inputHeightSpace = 7.5;
export var inputSmallHeightSpace = 5;
var topGutterSpace = 3;
var prefixMarginSpace = 1;
var placeholderTypographyName = "nano2_u";
var iconSizeSpace = 2;
var tooltipWidthSpace = 2;
export var iconVisibilityBreakpoint = "medium";
var loaderWidthSpace = 3.5;

/**
 * 1. Overwrites value set by Gatsby
 * */
export var inputBaseStyles = function inputBaseStyles(onColor, size, isBold, isSelect, borderRadius) {
  return css(function (_ref) {
    var theme = _ref.theme;
    var inputSizeHeightSpace = size === "small" ? inputSmallHeightSpace : inputHeightSpace;
    return css(["", ";width:100%;height:", ";padding:0 ", ";border:", " solid ", ";border-radius:", ";letter-spacing:normal;background:", ";", ""], cssTextAtm({
      typography: typographyName,
      onColor: backgroundColor,
      emphasis: typographyInactiveEmphasis,
      isBold: isBold
    }), space(inputSizeHeightSpace), space(horizontalGutterSpace), unit(borderWidth), getBorderColor(theme, onColor, "extraLow"), getBorderRadius(theme, borderRadius || "large"), getColor(theme, backgroundColor), isSelect && css(["height:auto;min-height:", ";padding-top:", ";padding-bottom:", ";", ""], space(inputSizeHeightSpace), space(2), space(2), size === "small" ? css(["padding-top:", ";padding-bottom:", ";"], space(1.5), space(1.5)) : null));
  });
};
var hoverStyles = function hoverStyles(onColor) {
  return css(function (_ref2) {
    var theme = _ref2.theme;
    return css(["border-color:", ";color:", ";"], getBorderColor(theme, onColor, "low"), getTextColor(theme, backgroundColor));
  });
};
export var inputBaseStylesWithHover = function inputBaseStylesWithHover(hasHover, hasFocus, onColor, size, isBold, isSelect, borderRadius) {
  return css(function () {
    return css(["", ";", ";", ""], transition("border-color", "color"), inputBaseStyles(onColor, size, isBold, isSelect, borderRadius), (hasHover || hasFocus) && css(["", ";"], hoverStyles(onColor)));
  });
};
export var inputExtendedStylesRaw = function inputExtendedStylesRaw(hasValue, hasFocus, hasError, hasSuccess, size, hasPlaceholder, isPlaceholderVisibleWhenHasValue) {
  return css(function (_ref3) {
    var theme = _ref3.theme;
    var errorBorderColor = getColor(theme, "error");
    var successBorderColor = getColor(theme, "success");
    return css(["", " ", " ", ""], hasPlaceholder && isPlaceholderVisibleWhenHasValue && hasValue && size !== "small" && css(["padding-top:", ";"], space(topGutterSpace)), hasError && !hasFocus && css(["border-color:", ";"], rgba(errorBorderColor, lowEmphasisOpacity)), hasSuccess && hasFocus && css(["border-color:", ";"], rgba(successBorderColor, lowEmphasisOpacity)));
  });
};
export var InputWrapper = styled.div.withConfig({
  componentId: "sc-1lt8p86-0"
})(["display:flex;position:relative;align-items:center;"]);
export var Prefix = styled.p.withConfig({
  componentId: "sc-1lt8p86-1"
})(function (_ref4) {
  var $hasPlaceholder = _ref4.$hasPlaceholder,
    $isPlaceholderVisibleWhenHasValue = _ref4.$isPlaceholderVisibleWhenHasValue,
    $width = _ref4.$width,
    $hasValue = _ref4.$hasValue;
  return css(["", ";", ";display:flex;position:absolute;top:", ";left:", ";align-items:center;height:", ";", " ", ""], transition("padding-top"), cssTextAtm({
    typography: typographyName,
    onColor: backgroundColor,
    emphasis: "low"
  }), unit(borderWidth * 2), space(horizontalGutterSpace), unit(inputHeightSpace * spaceFactor - borderWidth * 2), !!$width && css(["width:", ";"], unit($width)), $hasPlaceholder && $isPlaceholderVisibleWhenHasValue && $hasValue && css(["padding-top:", ";"], space(topGutterSpace)));
});
export var SuffixWrapper = styled.div.withConfig({
  componentId: "sc-1lt8p86-2"
})(function (_ref5) {
  var $hasPlaceholder = _ref5.$hasPlaceholder,
    $isPlaceholderVisibleWhenHasValue = _ref5.$isPlaceholderVisibleWhenHasValue,
    $hasValue = _ref5.$hasValue;
  return css(["", ";display:flex;position:absolute;top:", ";left:", ";align-items:center;width:calc(100% - ", ");height:", ";opacity:0;padding-right:", ";overflow:hidden;pointer-events:none;", " ", ""], transition("padding-top", "opacity"), unit(borderWidth * 2), unit(horizontalGutterSpace * spaceFactor), space(horizontalGutterSpace), unit(inputHeightSpace * spaceFactor - borderWidth * 2), space(horizontalGutterSpace), $hasValue && css(["space(topGutterSpace)};opacity:1;"]), $hasPlaceholder && $hasValue && css(["padding-top:", ";"], $isPlaceholderVisibleWhenHasValue && space(topGutterSpace)));
});
export var SuffixInputTextPlaceholder = styled.p.withConfig({
  componentId: "sc-1lt8p86-3"
})(function (_ref6) {
  var $suffixWidth = _ref6.$suffixWidth,
    $isLoading = _ref6.$isLoading;
  return css(["", ";height:0;visibility:hidden;overflow:hidden;", ""], cssTextAtm({
    typography: typographyName,
    onColor: backgroundColor,
    emphasis: suffixTypographyEmphasis,
    isBold: true
  }), $suffixWidth && css(["max-width:calc(100% - ", ");", ""], unit($suffixWidth), $isLoading && css(["max-width:calc( 100% - ", " );"], unit($suffixWidth + (loaderWidthSpace + horizontalGutterSpace) * spaceFactor))));
});
export var Suffix = styled.p.withConfig({
  componentId: "sc-1lt8p86-4"
})(["", ";flex-shrink:0;"], cssTextAtm({
  typography: typographyName,
  onColor: backgroundColor,
  emphasis: suffixTypographyEmphasis
}));
export var StyledTooltip = styled(TooltipMol).withConfig({
  componentId: "sc-1lt8p86-5"
})(["position:absolute;right:", ";"], space(horizontalGutterSpace));
export var StyledIcon = styled(IconAtm).withConfig({
  componentId: "sc-1lt8p86-6"
})(["position:absolute;top:50%;left:", ";transform:translateY(-50%);"], unit(horizontalGutterSpace * spaceFactor + borderWidth));
export var CtaWrapper = styled.div.withConfig({
  componentId: "sc-1lt8p86-7"
})(["display:flex;position:absolute;top:50%;right:", ";align-items:center;transform:translateY(-50%);"], unit(horizontalGutterSpace * spaceFactor + borderWidth));
export var PlaceholderWrapper = styled.div.withConfig({
  componentId: "sc-1lt8p86-8"
})(function (_ref7) {
  var $hasValue = _ref7.$hasValue,
    $size = _ref7.$size;
  return css(["display:flex;position:absolute;z-index:-1;left:", ";align-items:center;opacity:0;", ""], space(horizontalGutterSpace), $hasValue && $size !== "small" && css(["", ";z-index:1;top:", ";opacity:1;"], transition("opacity", "top", "z-index"), space(1)));
});
export var PlaceholderText = styled.p.withConfig({
  componentId: "sc-1lt8p86-9"
})(function (_ref8) {
  var $hasValue = _ref8.$hasValue,
    $hasError = _ref8.$hasError,
    $hasSuccess = _ref8.$hasSuccess;
  return css(["", ";", " ", " ", ""], transition("color", "font-size"), $hasValue && css(["", ";"], cssTextAtm({
    typography: placeholderTypographyName,
    onColor: backgroundColor,
    emphasis: typographyInactiveEmphasis
  })), $hasError && css(["", ";"], cssTextAtm({
    typography: placeholderTypographyName,
    color: "error"
  })), $hasSuccess && css(["", ";"], cssTextAtm({
    typography: placeholderTypographyName,
    color: "success"
  })));
});
export var PlaceholderIcon = styled(IconAtm).withConfig({
  componentId: "sc-1lt8p86-10"
})(["margin-left:", ";"], space(1));
export var Loader = styled(LoaderSVG).withConfig({
  componentId: "sc-1lt8p86-11"
})(function (_ref9) {
  var $hasCtaLink = _ref9.$hasCtaLink,
    $hasTooltip = _ref9.$hasTooltip,
    $hasFileUpload = _ref9.$hasFileUpload,
    theme = _ref9.theme;
  return css(["position:absolute;top:50%;right:", ";transform:translateY(-50%);width:", ";color:", ";", " ", ""], unit(horizontalGutterSpace * spaceFactor + borderWidth), space(loaderWidthSpace), getColor(theme, "secondaryLight"), ($hasCtaLink || $hasFileUpload) && css(["right:initial;left:", ";"], unit(horizontalGutterSpace * spaceFactor + borderWidth)), $hasTooltip && css(["right:", ";"], unit((tooltipWidthSpace + 1.5 * horizontalGutterSpace) * spaceFactor + borderWidth)));
});
export var StyledFileUploadInput = styled.label.withConfig({
  componentId: "sc-1lt8p86-12"
})(function (_ref10) {
  var $hasValue = _ref10.$hasValue,
    $hasFocus = _ref10.$hasFocus,
    $hasError = _ref10.$hasError,
    $hasSuccess = _ref10.$hasSuccess,
    $ctaWrapperWidth = _ref10.$ctaWrapperWidth,
    $onColor = _ref10.$onColor,
    $size = _ref10.$size,
    $hasPlaceholder = _ref10.$hasPlaceholder,
    $isPlaceholderVisibleWhenHasValue = _ref10.$isPlaceholderVisibleWhenHasValue;
  return css(["", ";", " ", ";display:flex;align-items:center;cursor:pointer;&:hover{", ";}", " ", " span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}"], inputBaseStyles($onColor, $size), inputExtendedStylesRaw($hasValue, $hasFocus, $hasError, $hasSuccess, $size, $hasPlaceholder, $isPlaceholderVisibleWhenHasValue), transition("border-color", "color"), hoverStyles($onColor), !$hasValue && css(["border-style:dashed;"]), $ctaWrapperWidth && css(["padding-right:", ";"], unit($ctaWrapperWidth + borderWidth + 2 * horizontalGutterSpace * spaceFactor)));
});

/**
 * 1. Style needed to properly align placeholder when hovered on Firefox
 * */
export var StyledInputMol = styled.input.withConfig({
  componentId: "sc-1lt8p86-13"
})(function (_ref11) {
  var $hasValue = _ref11.$hasValue,
    $hasPrefix = _ref11.$hasPrefix,
    $hasSuffix = _ref11.$hasSuffix,
    $isPlaceholderVisibleWhenHasValue = _ref11.$isPlaceholderVisibleWhenHasValue,
    $hasHiddenBorder = _ref11.$hasHiddenBorder,
    $hasHover = _ref11.$hasHover,
    $hasFocus = _ref11.$hasFocus,
    $isPassword = _ref11.$isPassword,
    $hasCtaLink = _ref11.$hasCtaLink,
    $hasCtaIcon = _ref11.$hasCtaIcon,
    $isLoading = _ref11.$isLoading,
    $hasError = _ref11.$hasError,
    $hasSuccess = _ref11.$hasSuccess,
    $shadow = _ref11.$shadow,
    $prefixWidth = _ref11.$prefixWidth,
    $suffixWidth = _ref11.$suffixWidth,
    $ctaWrapperWidth = _ref11.$ctaWrapperWidth,
    $hasTooltip = _ref11.$hasTooltip,
    $hasIcon = _ref11.$hasIcon,
    $onColor = _ref11.$onColor,
    $backgroundColor = _ref11.$backgroundColor,
    $size = _ref11.$size,
    $hasPlaceholder = _ref11.$hasPlaceholder,
    $isFileUpload = _ref11.$isFileUpload,
    $readOnly = _ref11.$readOnly,
    $hasHiddenNumberControls = _ref11.$hasHiddenNumberControls,
    $hasCenteredValue = _ref11.$hasCenteredValue,
    theme = _ref11.theme;
  var hasBoldText = $hasValue && $hasSuffix;
  return css(["", ";", ";", " ", " &::placeholder{", ";}&[list]::-webkit-calendar-picker-indicator{display:none;}", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", " ", ""], transition("border-color", "color", "padding"), inputBaseStylesWithHover($hasHover, $hasFocus, $onColor, $size, hasBoldText), inputExtendedStylesRaw($hasValue, $hasFocus, $hasError, $hasSuccess, $size, $hasPlaceholder, $isPlaceholderVisibleWhenHasValue), $backgroundColor ? css(["background:", ";"], getColor(theme, $backgroundColor)) : null, transition("color"), $shadow && css(["box-shadow:", ";"], getBoxShadow(theme, $shadow)), ($hasHover || $hasFocus) && css(["&::placeholder{color:", " line-height:", ";}"], getTextColor(theme, backgroundColor), unit(inputHeightSpace * spaceFactor - borderWidth * 2)), $hasHiddenBorder && css(["border:none;"]), $isLoading && css(["", ""], !$hasCtaLink && !$hasTooltip && css(["padding-right:", ";"], unit((loaderWidthSpace + horizontalGutterSpace * 2) * spaceFactor))), $hasPrefix && $prefixWidth && css(["", ";padding-left:", ";"], transition("padding-left"), unit($prefixWidth + horizontalGutterSpace * spaceFactor + prefixMarginSpace * spaceFactor)), $hasSuffix && $suffixWidth && css(["", ";padding-right:", ";", " ", ""], transition("padding-right"), unit($suffixWidth + horizontalGutterSpace * spaceFactor), $isLoading && css(["padding-right:", ";"], unit($suffixWidth + (loaderWidthSpace + horizontalGutterSpace * 2) * spaceFactor)), !$hasFocus && css(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"])), $isPassword && $ctaWrapperWidth && css(["padding-right:", ";"], unit($ctaWrapperWidth + borderWidth + 2 * horizontalGutterSpace * spaceFactor)), $hasCtaIcon && $ctaWrapperWidth && css(["padding-right:", ";"], unit($ctaWrapperWidth + borderWidth + 2 * horizontalGutterSpace * spaceFactor)), $hasTooltip && css(["padding-right:", ";", ""], space(2 * horizontalGutterSpace + tooltipWidthSpace), $isLoading && css(["padding-right:", ";"], unit((loaderWidthSpace + tooltipWidthSpace + horizontalGutterSpace * 2) * spaceFactor))), $hasCtaLink && css(["", ""], $isLoading && css(["color:transparent;&::placeholder{color:transparent;}"])), $hasIcon && css(["", "{padding-left:", ";}"], breakpoint(iconVisibilityBreakpoint), space(2 * horizontalGutterSpace + iconSizeSpace)), $isFileUpload && css(["display:none;"]), $readOnly && css(["background:", ";"], getColor(theme, "background")), $hasHiddenNumberControls ? css(["&::-webkit-outer-spin-button,&::-webkit-inner-spin-button{-webkit-appearance:none;margin:0;}&[type=\"number\"]{-moz-appearance:textfield;}"]) : null, $hasCenteredValue ? css(["text-align:center;"]) : null);
});